import adminRoutes, { homeRoute, locationRoute, organizationRoute } from './admin';
import {
    canAccessAdminPath,
    COACH_SIDEBAR_PATHS,
    isCoach,
} from './access-policy';
import { UserRole } from '../models/organization/dto/invite-to-organization.dto';
import { RType } from './types';

/**
 * Route-table-aware access helpers. This module imports the REAL admin route
 * table (and therefore every page component) — only layout-level code should
 * import it. Leaf components and hooks use `./access-policy`, which is pure
 * logic with no heavy imports; everything there is re-exported here so
 * layout-level consumers have a single import surface.
 */
export * from './access-policy';

/** The admin-layout routes this role is allowed to render, hidden ones included. */
export function accessibleAdminRoutes(role: UserRole): RType[] {
    if (canAccessAdminPath(role, '/home')) return adminRoutes;
    return adminRoutes.filter((route) => canAccessAdminPath(role, route.path));
}

/**
 * The sidebar for a given role and selection state.
 *
 * Admins get progressive disclosure: an org has to be picked before locations
 * appear, and a location before the rest of the dashboard does. Coaches skip
 * that ladder entirely — they can't reach the organization or locations pages
 * that the ladder exists to funnel them through, and both pickers auto-select
 * (see `OrganizationPicker` / `SearchBar`), so gating their three sections on
 * selection state would just blank the sidebar on first paint.
 *
 * The coach entries are listed in `COACH_SIDEBAR_PATHS` order and stripped of
 * their `group` key: three items scattered across the "Organisation" and
 * "Contenu" collapsible sections is more chrome than content, and
 * `SidebarLinks` renders a flat list when nothing carries a group.
 */
export function visibleAdminRoutes({
    role,
    hasOrganization,
    hasLocation,
}: {
    role: UserRole;
    hasOrganization: boolean;
    hasLocation: boolean;
}): RType[] {
    if (isCoach(role))
        return COACH_SIDEBAR_PATHS
            .map((path) => adminRoutes.find((route) => route.path === path))
            .filter((route): route is RType => route != null && !route.hidden)
            .map(({ group, ...route }) => route);
    if (hasLocation) return adminRoutes.filter((route) => !route.hidden);
    if (hasOrganization) return [homeRoute, organizationRoute, locationRoute];
    return [homeRoute, organizationRoute];
}

/**
 * Drop platform-staff routes (`requiresElevatedAdmin`) for everyone else.
 * Applied in `useVisibleRoutes`, the single source of BOTH the sidebar and the
 * command palette — filtering in the sidebar alone left the entry in the
 * palette for every org admin. Cosmetic: the page and the API both refuse
 * non-platform admins. `undefined` (check still loading) hides them too.
 */
export function withoutElevatedOnlyRoutes(
    routes: RType[],
    isElevatedAdmin: boolean | undefined,
): RType[] {
    return isElevatedAdmin === true
        ? routes
        : routes.filter((route) => !route.requiresElevatedAdmin);
}
